import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Image, Text, TouchableOpacity, ScrollView, Pressable } from 'react-native-web';
import Modal from "react-modal";
import ReactPlayer from "react-player/lazy";
import StarRating from './StarRating';
import { getCreatorsContent, getCreatorsContentByAesthetic } from "../services/CreatorService";
import xIcon from "../icons/DupeIcons-16-Biz-Unavailable.png";

const CreatorModal = ({
    isOpen,
    onClose,
    selectedCreator,
    creatorsAesthetics,
    afterOpenModal,
    initialAesthetic = null
}) => {
    const [creatorsContent, setCreatorsContent] = useState([]);
    const [selectedContent, setSelectedContent] = useState(null);
    const [selectedAesthetic, setSelectedAesthetic] = useState(null);
    const [currentContentIndex, setCurrentContentIndex] = useState(0);
    const [allCreatorContent, setAllCreatorContent] = useState([]);

    // Add CSS for video styling
    const videoStyles = `
        .creator-content-video {
            border-radius: 8px !important;
            margin: 0;
            padding: 0;
            display: block;
            position: relative;
            width: 100%;
            height: 100%;
        }
        
        .creator-content-video video {
            border-radius: 8px !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block;
            position: relative;
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
        }
        
        .creator-content-video > div {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0 !important;
            padding: 0 !important;
            position: relative;
            width: 100% !important;
            height: 100% !important;
        }
        
        .creator-selected-content-video {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            position: relative;
            max-width: 600px;
            margin: 0 auto;
        }
        
        .creator-selected-content-video video {
            border-radius: 8px !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block;
            position: relative;
            width: auto !important;
            height: 100% !important;
            object-fit: contain !important;
        }
        
        .creator-selected-content-video > div {
            border-radius: 8px !important;
            overflow: hidden;
            margin: 0 !important;
            padding: 0 !important;
            position: relative;
            width: auto !important;
        }
    `;

    // Inject styles into head
    React.useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = videoStyles;
        document.head.appendChild(styleSheet);
        return () => document.head.removeChild(styleSheet);
    }, []);

    // Helper function to create unified content array for navigation
    function createUnifiedCreatorContentArray(content) {
        const photos = (content || []).filter(item => item.content_type !== "VIDEO").map(item => ({
            ...item,
            type: 'photo',
            url: `https://d3p3fw3rutb1if.cloudfront.net/photos/${item.img_preview_id}`,
            content_type: 'PHOTO'
        }));
        const videos = (content || []).filter(item => item.content_type === "VIDEO").map(item => ({
            ...item,
            type: 'video',
            url: `https://d3p3fw3rutb1if.cloudfront.net/videos/${item.img_preview_id}`,
            content_type: 'VIDEO'
        }));
        return [...photos, ...videos];
    }

    // Helper function to find content index
    function findCreatorContentIndex(content, allContentArray) {
        return allContentArray.findIndex(item => 
            item.img_preview_id === content.img_preview_id && 
            item.content_type === content.content_type
        );
    }

    // Navigation functions for creator content
    function navigateToCreatorContent(index) {
        if (index >= 0 && index < allCreatorContent.length) {
            setCurrentContentIndex(index);
            setSelectedContent(allCreatorContent[index]);
        }
    }

    function navigateToCreatorPrevious() {
        const newIndex = currentContentIndex > 0 ? currentContentIndex - 1 : allCreatorContent.length - 1;
        navigateToCreatorContent(newIndex);
    }

    function navigateToCreatorNext() {
        const newIndex = currentContentIndex < allCreatorContent.length - 1 ? currentContentIndex + 1 : 0;
        navigateToCreatorContent(newIndex);
    }

    // Keyboard navigation handler for creator content
    function handleCreatorKeyNavigation(event) {
        if (!selectedContent || !isOpen) return;
        
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                navigateToCreatorPrevious();
                break;
            case 'ArrowRight':
                event.preventDefault();
                navigateToCreatorNext();
                break;
            case 'Escape':
                event.preventDefault();
                setSelectedContent(null);
                break;
        }
    }

    function findAestheticContent(aesthetic) {
        // If the same aesthetic is clicked again, clear the filter
        if (selectedAesthetic === aesthetic) {
            setSelectedAesthetic(null);
            getCreatorsContent(selectedCreator.id)
                .then(resp => {
                    if (resp.status === 200 && resp.data) {
                        setCreatorsContent(resp.data);
                        setAllCreatorContent(resp.data);
                        setSelectedContent(null);
                    }
                })
        } else {
            // Apply the new aesthetic filter
            setSelectedAesthetic(aesthetic);
            getCreatorsContentByAesthetic(selectedCreator.id, aesthetic)
                .then(resp => {
                    if (resp.status === 200 && resp.data) {
                        setCreatorsContent(resp.data);
                        setAllCreatorContent(resp.data);
                        setSelectedContent(null);
                    }
                })
        }
    }

    // Add keyboard navigation when creator modal is open
    useEffect(() => {
        if (isOpen && selectedContent) {
            document.addEventListener('keydown', handleCreatorKeyNavigation);
            return () => {
                document.removeEventListener('keydown', handleCreatorKeyNavigation);
            };
        }
    }, [isOpen, selectedContent, currentContentIndex, allCreatorContent]);

    // Initialize content when creator modal opens or content changes
    useEffect(() => {
        if (isOpen && creatorsContent) {
            const unified = createUnifiedCreatorContentArray(creatorsContent);
            setAllCreatorContent(unified);
            setCurrentContentIndex(0);
        }
    }, [isOpen, creatorsContent]);

    // Reset state when creator modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedContent(null);
            setCurrentContentIndex(0);
            setAllCreatorContent([]);
            setSelectedAesthetic(null);
            setCreatorsContent([]);
        }
    }, [isOpen]);

    // Clear content when creator changes to prevent showing stale data
    useEffect(() => {
        if (isOpen && selectedCreator) {
            // Immediately clear old content when creator changes
            setCreatorsContent([]);
            setSelectedContent(null);
        }
    }, [isOpen, selectedCreator?.id]);

    // Load initial content when modal opens
    useEffect(() => {
        if (isOpen && selectedCreator) {
            // If an initial aesthetic is provided, load filtered content
            if (initialAesthetic) {
                setSelectedAesthetic(initialAesthetic);
                getCreatorsContentByAesthetic(selectedCreator.id, initialAesthetic)
                    .then(resp => {
                        if (resp.status === 200 && resp.data) {
                            setCreatorsContent(resp.data);
                        }
                    })
            } else {
                // Otherwise load all content
                getCreatorsContent(selectedCreator.id)
                    .then(resp => {
                        if (resp.status === 200 && resp.data) {
                            setCreatorsContent(resp.data);
                        }
                    })
            }
        }
    }, [isOpen, selectedCreator, initialAesthetic]);

    if (!selectedCreator) return null;

    return (
        <Modal
            isOpen={isOpen}
            onAfterOpen={afterOpenModal}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            className={"invite-modal"}
            style={styles.modal}
        >
            <View style={styles.creatorModalContainer}>
                <ScrollView
                    style={styles.creatorModalScrollView}
                    showsVerticalScrollIndicator={true}
                    bounces={false}
                >
                    <View style={styles.creatorModalBody}>
                        <View style={styles.creatorModalHeader}>
                            <Pressable
                                style={({ hovered }) => [
                                    styles.creatorModalHeaderContent,
                                    hovered && styles.creatorModalHeaderContentHovered
                                ]}
                                onPress={() => window.open("https://dupephotos.com/profile/" + selectedCreator.username, "_blank")}
                                accessible={true}
                                accessibilityLabel={`View ${selectedCreator.first_name} ${selectedCreator.last_name} profile on Dupe Photos`}
                                accessibilityRole="button"
                            >
                                <Image
                                    style={styles.creatorModalHeaderProfileImage}
                                    source={{uri: "https://d3p3fw3rutb1if.cloudfront.net/users-preview/" + selectedCreator.id}}
                                />
                                <View style={styles.creatorModalHeaderLeft}>
                                    <View style={styles.creatorModalHeaderNameRow}>
                                        <Text style={styles.creatorModalTitle}>
                                            {selectedCreator.first_name} {selectedCreator.last_name}
                                        </Text>
                                        {selectedCreator.country && (
                                            <Text style={styles.creatorModalCountryText}>
                                                {selectedCreator.country}
                                            </Text>
                                        )}
                                    </View>
                                    <Text style={styles.creatorNameSubtitle}>
                                        @{selectedCreator.username}
                                    </Text>
                                    <View style={styles.creatorModalHeaderRatings}>
                                        {(selectedCreator.avg_quality_rating !== null && selectedCreator.avg_responsiveness_rating !== null) && (selectedCreator.avg_quality_rating !== undefined && selectedCreator.avg_responsiveness_rating !== undefined) ? (
                                            <View style={styles.creatorModalHeaderRatingsRow}>
                                                <StarRating rating={selectedCreator.avg_responsiveness_rating} label="Responsiveness" />
                                                <StarRating rating={selectedCreator.avg_quality_rating} label="Quality" />
                                            </View>
                                        ) : (
                                            <Text style={styles.creatorModalHeaderNoRatingText}>This creator has not been rated yet.</Text>
                                        )}
                                    </View>
                                    {creatorsAesthetics && creatorsAesthetics.length > 0 && (
                                        <View style={styles.creatorModalHeaderAesthetics}>
                                            <ScrollView
                                                horizontal={true}
                                                showsHorizontalScrollIndicator={false}
                                                style={styles.creatorModalAestheticsScrollView}
                                                contentContainerStyle={styles.creatorModalAestheticsContent}
                                            >
                                                {creatorsAesthetics.map((aesthetic, index) => (
                                                    <Pressable
                                                        key={index}
                                                        style={({ hovered }) => [
                                                            styles.creatorModalAestheticTag,
                                                            selectedAesthetic === aesthetic && styles.creatorModalAestheticTagActive,
                                                            hovered && styles.creatorModalAestheticTagHovered
                                                        ]}
                                                        onPress={(e) => {
                                                            e.stopPropagation();
                                                            findAestheticContent(aesthetic);
                                                        }}
                                                        accessible={true}
                                                        accessibilityLabel={`View ${aesthetic} content`}
                                                        accessibilityRole="button"
                                                    >
                                                        <Text style={[styles.creatorModalAestheticText, selectedAesthetic === aesthetic && {color: 'white'}]} numberOfLines={1}>
                                                            {aesthetic}
                                                        </Text>
                                                    </Pressable>
                                                ))}
                                            </ScrollView>
                                        </View>
                                    )}
                                </View>
                            </Pressable>
                            <Pressable
                                onPress={onClose}
                                style={({ hovered }) => [
                                    styles.closeButton,
                                    hovered && styles.closeButtonHovered
                                ]}
                                accessible={true}
                                accessibilityLabel="Close creator modal"
                                accessibilityRole="button"
                            >
                                <Image source={xIcon} style={styles.closeIcon} />
                            </Pressable>
                        </View>

                        <View style={styles.creatorModalLayout}>
                            {!selectedContent && (
                                <View style={styles.creatorContentGalleryFullWidth}>
                                    {creatorsContent && creatorsContent.length > 0 && (
                                        <View style={styles.creatorContentSection}>
                                            <Text style={styles.creatorContentSectionTitle}>Content</Text>
                                            <View style={styles.creatorContentGrid}>
                                                {[0, 1, 2].map(rowIndex => {
                                                    const rowItems = creatorsContent.slice(rowIndex * 3, (rowIndex + 1) * 3);
                                                    if (rowItems.length === 0) return null;
                                                    
                                                    return (
                                                        <View key={rowIndex} style={styles.creatorContentGridRow}>
                                                            {rowItems.map((content, colIndex) => {
                                                                const index = rowIndex * 3 + colIndex;
                                                                return (
                                                                    <Pressable
                                                                        key={index}
                                                                        onPress={() => {
                                                                            const contentIndex = findCreatorContentIndex(content, allCreatorContent);
                                                                            setCurrentContentIndex(contentIndex);
                                                                            setSelectedContent(content);
                                                                        }}
                                                                        style={styles.creatorContentGridItem}
                                                                    >
                                                                        {content.content_type === "VIDEO" ? (
                                                                            <View style={styles.creatorVideoThumbnailContainer}>
                                                                                <ReactPlayer
                                                                                    width="100%"
                                                                                    height="100%"
                                                                                    className="creator-content-video"
                                                                                    url={`https://d3p3fw3rutb1if.cloudfront.net/videos/${content.img_preview_id}`}
                                                                                    muted={true}
                                                                                    playing={true}
                                                                                    loop={true}
                                                                                    controls={false}
                                                                                />
                                                                            </View>
                                                                        ) : (
                                                                            <Image
                                                                                source={{uri: `https://d3p3fw3rutb1if.cloudfront.net/photos/${content.img_preview_id}`}}
                                                                                style={styles.creatorContentGridImage}
                                                                                resizeMode="cover"
                                                                            />
                                                                        )}
                                                                    </Pressable>
                                                                );
                                                            })}
                                                            {/* Fill empty spots in the row */}
                                                            {rowItems.length < 3 && Array.from({ length: 3 - rowItems.length }).map((_, emptyIndex) => (
                                                                <View key={`empty-${rowIndex}-${emptyIndex}`} style={[styles.creatorContentGridItem, { opacity: 0 }]} />
                                                            ))}
                                                        </View>
                                                    );
                                                })}
                                            </View>
                                            {creatorsContent.length > 9 && (
                                                <Text style={styles.moreContentText}>
                                                    +{creatorsContent.length - 9} more items available
                                                </Text>
                                            )}
                                        </View>
                                    )}
                                    {(!creatorsContent || creatorsContent.length === 0) && (
                                        <View style={styles.noContentContainer}>
                                            <Text style={styles.noContentText}>No content available</Text>
                                        </View>
                                    )}
                                </View>
                            )}

                            {selectedContent && (
                                <View style={styles.creatorSelectedContentViewFullWidth}>
                                    <View style={styles.creatorSelectedContentHeader}>
                                        <TouchableOpacity onPress={() => setSelectedContent(null)} style={styles.creatorBackButton}>
                                            <Text style={styles.creatorBackButtonText}>← Back to Gallery</Text>
                                        </TouchableOpacity>
                                        <View style={styles.creatorContentCounter}>
                                            <Text style={styles.creatorContentCounterText}>
                                                {currentContentIndex + 1} of {allCreatorContent.length}
                                            </Text>
                                            <Text style={styles.creatorKeyboardHintText}>
                                                Use ← → keys to navigate
                                            </Text>
                                        </View>
                                    </View>
                                    <View style={styles.creatorSelectedContentContainer}>
                                        {/* Previous Arrow */}
                                        <Pressable 
                                            style={({ hovered }) => [
                                                styles.creatorNavigationArrow, 
                                                styles.creatorPreviousArrow,
                                                hovered && styles.creatorNavigationArrowHovered
                                            ]}
                                            onPress={navigateToCreatorPrevious}
                                            accessible={true}
                                            accessibilityLabel="Previous content"
                                            accessibilityRole="button"
                                        >
                                            <Text style={styles.creatorArrowText}>‹</Text>
                                        </Pressable>

                                        {/* Content Display */}
                                        <View style={styles.creatorContentDisplayArea}>
                                            {selectedContent.content_type === "VIDEO" && (
                                                <ReactPlayer
                                                    width="100%"
                                                    height="400px"
                                                    className="creator-selected-content-video"
                                                    url={`https://d3p3fw3rutb1if.cloudfront.net/videos/${selectedContent.img_preview_id}`}
                                                    muted={false}
                                                    playing={true}
                                                    loop={true}
                                                    controls={true}
                                                />
                                            )}
                                            {selectedContent.content_type !== "VIDEO" && (
                                                <img
                                                    className="selected-creator-content-image"
                                                    src={`https://d3p3fw3rutb1if.cloudfront.net/photos/${selectedContent.img_preview_id}`}
                                                    alt="Selected content"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '400px',
                                                        height: 'auto',
                                                        width: 'auto',
                                                        borderRadius: '8px',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            )}
                                        </View>

                                        {/* Next Arrow */}
                                        <Pressable 
                                            style={({ hovered }) => [
                                                styles.creatorNavigationArrow, 
                                                styles.creatorNextArrow,
                                                hovered && styles.creatorNavigationArrowHovered
                                            ]}
                                            onPress={navigateToCreatorNext}
                                            accessible={true}
                                            accessibilityLabel="Next content"
                                            accessibilityRole="button"
                                        >
                                            <Text style={styles.creatorArrowText}>›</Text>
                                        </Pressable>
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                </ScrollView>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modal: {
        overlay: {
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            backgroundColor: 'rgba(57, 109, 224, .5)'
        },
        content: {
            margin: '25%',
            inset: 0,
            background: 'none',
            flex: 1,
            border: 'none',
            alignSelf: 'center',
            alignContent: 'center',
            outline: 'none',
        }
    },
    creatorModalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: '100%',
        height: '100%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    creatorModalScrollView: {
        flex: 1,
        minHeight: 0,
    },
    creatorModalBody: {
        padding: 25,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    creatorModalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 25,
        paddingBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
        flexShrink: 0,
    },
    creatorModalHeaderContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        borderRadius: 8,
        padding: 8,
        margin: -8,
        transition: 'all 0.2s ease',
        outlineStyle: 'none',
        borderWidth: 0,
        cursor: 'pointer',
    },
    creatorModalHeaderContentHovered: {
        backgroundColor: '#f9fafb',
        transform: [{ scale: 1.01 }],
    },
    creatorModalHeaderProfileImage: {
        width: 80,
        height: 80,
        borderRadius: 40,
        marginRight: 16,
        backgroundColor: '#f3f4f6',
        borderWidth: 2,
        borderColor: '#e5e7eb',
    },
    creatorModalHeaderLeft: {
        flex: 1,
    },
    creatorModalHeaderNameRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexWrap: 'wrap',
    },
    creatorModalCountryText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: "#6b7280",
        marginLeft: 8,
        fontWeight: '400',
    },
    creatorModalHeaderRatings: {
        marginTop: 8,
    },
    creatorModalHeaderRatingsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 15,
    },
    creatorModalHeaderNoRatingText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: "#9ca3af",
        fontStyle: 'italic',
        fontWeight: '400',
    },
    creatorModalHeaderAesthetics: {
        marginTop: 12,
    },
    creatorModalTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 28,
        fontWeight: 'bold',
        color: '#396de0',
    },
    creatorNameSubtitle: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#6b7280',
        fontWeight: '500',
    },
    closeButton: {
        padding: 8,
        borderRadius: 6,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    closeButtonHovered: {
        backgroundColor: '#f3f4f6',
    },
    closeIcon: {
        width: 20,
        height: 20,
        tintColor: '#6b7280',
    },
    creatorModalLayout: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    creatorContentGalleryFullWidth: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    creatorContentSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    creatorContentSectionTitle: {
        fontFamily: "'forma-djr-display', sans-serif",
        fontSize: 18,
        fontWeight: 'bold',
        color: '#396de0',
        marginBottom: 15,
        flexShrink: 0,
    },
    creatorContentGrid: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        width: '100%',
        minHeight: 0,
    },
    creatorContentGridRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'stretch',
        flex: 1,
        marginBottom: 8,
    },
    creatorContentGridItem: {
        width: `calc(25% - 5.33px)`,
        aspectRatio: 1,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: '#f8f8f8',
        flex: '0 0 auto',
    },
    creatorContentGridImage: {
        width: '100%',
        height: '100%',
        borderRadius: 6,
    },
    creatorVideoThumbnailContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 6,
    },
    creatorSelectedContentViewFullWidth: {
        flex: 1,
    },
    creatorSelectedContentHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    creatorBackButton: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 8,
    },
    creatorBackButtonText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#396de0',
        fontWeight: '600',
    },
    creatorSelectedContentContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f8f8',
        borderRadius: 10,
        padding: 20,
        minHeight: 400,
        width: '100%',
        position: 'relative',
    },
    creatorContentDisplayArea: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 20,
    },
    creatorContentCounter: {
        backgroundColor: '#f3f4f6',
        borderRadius: 20,
        paddingVertical: 6,
        paddingHorizontal: 12,
    },
    creatorContentCounterText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        fontWeight: '500',
    },
    creatorKeyboardHintText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 12,
        color: '#9ca3af',
        fontWeight: '400',
        marginTop: 2,
        textAlign: 'center',
    },
    creatorNavigationArrow: {
        width: 75,
        height: 75,
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        zIndex: 10,
        outlineStyle: 'none',
        borderWidth: 0,
    },
    creatorPreviousArrow: {
        marginRight: 10,
    },
    creatorNextArrow: {
        marginLeft: 10,
    },
    creatorArrowText: {
        fontSize: 48,
        color: '#396de0',
        fontWeight: 'bold',
        lineHeight: 48,
        textAlign: 'center',
    },
    creatorNavigationArrowHovered: {
        transform: [{ scale: 1.2 }],
    },
    creatorModalAestheticsScrollView: {
        marginHorizontal: -2,
    },
    creatorModalAestheticsContent: {
        paddingHorizontal: 2,
        gap: 8,
        alignItems: 'center',
    },
    creatorModalAestheticTag: {
        backgroundColor: '#f3f4f6',
        borderRadius: 12,
        paddingVertical: 6,
        paddingHorizontal: 12,
        marginRight: 8,
        transition: 'all 0.2s ease',
        borderColor: 'transparent',
    },
    creatorModalAestheticTagActive: {
        backgroundColor: '#396de0',
        borderColor: '#396de0',
    },
    creatorModalAestheticTagHovered: {
        backgroundColor: '#e5e7eb',
        transform: [{ scale: 1.05 }],
    },
    creatorModalAestheticText: {
        fontSize: 12,
        color: '#6b7280',
        fontFamily: "'lato', sans-serif",
        fontWeight: '500',
        whiteSpace: 'nowrap',
    },
    moreContentText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#6b7280',
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: 10,
        fontWeight: '400',
        flexShrink: 0,
    },
    noContentContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        backgroundColor: '#f8f9ff',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#e5e7eb',
        borderStyle: 'dashed',
    },
    noContentText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 16,
        color: '#9ca3af',
        fontStyle: 'italic',
    },
});

export default CreatorModal; 